import AppRegistrationIcon from '@mui/icons-material/AppRegistration'
import CloseIcon from '@mui/icons-material/Close'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import TvIcon from '@mui/icons-material/Tv'
import { Dialog, MenuItem, Select } from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, platformWeb } from 'src/consumer/services'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { CAFlowSelectionDialogBox, getNameOfVirtualChamberRoom } from '../CAHomepageComponents'
import { CAFlowSelectionDialogContinueButton } from '../ClinicHomepageStyles'
import useAumhumScreen from '../hooks/useAumhumScreen'

// Separate component for flow selection options
// Separate component for appointment screen configuration
const AppointmentScreenConfig = ({
  loading,
  selectedBrand,
  selectedLocation,
  selectedMentor,
  onBrandChange,
  onLocationChange,
  onMentorChange,
  brands,
  locations,
  mentors,
  assistantData,
}) => {
  if (loading) return <Spinner size="sm" variant="dark" />

  return (
    <div className="d-flex flex-column gap-2 w-100" style={{ marginTop: '16px' }}>
      <div>Select a Clinic:</div>
      <Select value={selectedBrand} onChange={(e) => onBrandChange(e.target.value)}>
        {brands?.map((item) => (
          <MenuItem key={item?.clinicBrandId} value={item?.clinicBrandId}>
            {item?.clinicBrandName}
          </MenuItem>
        ))}
      </Select>

      <div>Select Clinic Location:</div>
      <Select value={selectedLocation} onChange={(e) => onLocationChange(e.target.value)}>
        {locations
          ?.filter((item) => item?.clinicBrandId === selectedBrand)
          ?.map((item) => (
            <MenuItem key={item?.id} value={item?.id}>
              {item?.name}
            </MenuItem>
          ))}
      </Select>

      <div>Select Doctor/Therapist:</div>
      <Select value={selectedMentor} onChange={(e) => onMentorChange(e.target.value)}>
        {mentors?.map((manager) => {
          const supportRoles =
            assistantData?.clinicAssistantProfileData?.find(
              (item) => item?.clinicId === selectedLocation,
            )?.supportRoles || []

          if (supportRoles?.some((item) => item?.managerId === manager?.id)) {
            return (
              <MenuItem key={manager?.id} value={manager?.id}>
                {manager?.name}
              </MenuItem>
            )
          }
          return null
        })}
      </Select>
    </div>
  )
}

// Separate component for remote doctor screen configuration
const RemoteDoctorConfig = ({
  loading,
  selectedScreenConfig,
  selectedVirtualRoom,
  onScreenConfigChange,
  onVirtualRoomChange,
  screenConfigs,
  virtualConfigs,
  assistantData,
}) => {
  if (loading) return <Spinner variant="dark" size="sm" />

  return (
    <div className="d-flex flex-column gap-2 w-100" style={{ marginTop: '16px' }}>
      <div>Select Screen Device Config:</div>
      <Select value={selectedScreenConfig} onChange={(e) => onScreenConfigChange(e.target.value)}>
        {screenConfigs?.map((item) => (
          <MenuItem key={item?.id} value={item?.id}>
            {item?.title}
          </MenuItem>
        ))}
      </Select>

      <div>Select Chamber Room:</div>
      <Select value={selectedVirtualRoom} onChange={(e) => onVirtualRoomChange(e.target.value)}>
        {virtualConfigs
          ?.filter((item) => item?.screenDeviceConfigId === selectedScreenConfig)
          ?.map((item) => (
            <MenuItem key={item?.virtualChamberRoomId} value={item?.virtualChamberRoomId}>
              {getNameOfVirtualChamberRoom(item, assistantData)}
            </MenuItem>
          ))}
      </Select>
    </div>
  )
}

// Custom hook for data management
const useCADeviceData = (assistantData) => {
  const [oohDataLoading, setOOHDataLoading] = useState(false)
  const [screenConfigLoading, setScreenConfigLoading] = useState(false)
  const [brands, setBrands] = useState([])
  const [locations, setLocations] = useState([])
  const [mentors, setMentors] = useState([])
  const [screenConfigs, setScreenConfigs] = useState([])
  const [virtualConfigs, setVirtualConfigs] = useState([])

  const setupOOHData = async () => {
    setOOHDataLoading(true)

    const allAssociatedClinics = assistantData?.associatedClinicsData || []
    const uniqueBrands = []

    // Extract unique brands
    allAssociatedClinics.forEach((clinic) => {
      if (!uniqueBrands.find((brand) => brand?.clinicBrandId === clinic?.clinicBrandId)) {
        uniqueBrands.push({
          clinicBrandId: clinic?.clinicBrandId,
          clinicBrandName: clinic?.clinicBrandName,
        })
      }
    })

    // Get all support roles and manager profiles
    const allSupportRoles = []
    assistantData?.clinicAssistantProfileData?.forEach((item) =>
      allSupportRoles.push(...(item?.supportRoles || [])),
    )

    const managerProfiles = []
    if (Array.isArray(allSupportRoles)) {
      const promises = allSupportRoles.map(async (supportRole) => {
        try {
          const response = await getCPDataWithoutParams(
            `/api/secure/clinic-assistant/service-provider?id=${
              supportRole?.managerId
            }&slug=&zoneOffset=${encodeURIComponent(
              getTimezoneOffset(),
            )}&appVersion=${appVersion}&platformType=${platformWeb}`,
          )
          if (response.status === 200) {
            return response?.data
          }
        } catch (error) {
          console.error('Error fetching manager profile:', error)
        }
        return null
      })

      const results = await Promise.all(promises)
      managerProfiles.push(...results.filter(Boolean))
    }

    setBrands(uniqueBrands)
    setLocations(allAssociatedClinics)
    setMentors(managerProfiles)
    setOOHDataLoading(false)
  }

  const setupDoctorScreenData = async () => {
    setScreenConfigLoading(true)
    setScreenConfigs(assistantData?.associatedScreenDeviceConfigs?.screenDeviceConfigsList || [])
    setVirtualConfigs(assistantData?.associatedScreenDeviceConfigs?.virtualDeviceConfigsList || [])
    setScreenConfigLoading(false)
  }

  useEffect(() => {
    if (assistantData) {
      setupOOHData()
      setupDoctorScreenData()
    }
  }, [assistantData])

  return {
    oohDataLoading,
    screenConfigLoading,
    brands,
    locations,
    mentors,
    screenConfigs,
    virtualConfigs,
  }
}

// Main component - now much cleaner and focused
export const CADeviceUseFixingComponent = ({ CAFlowDialogOpen, setCAFlowDialogOpen }) => {
  const { assistantData } = useContext(ClinicDataContext)
  const { registerScreen, unregisterScreen } = useAumhumScreen()
  // Determine base path based on current location, not role - portals are separate
  const currentPath = window.location.pathname
  const basePath = currentPath.startsWith('/kiosk') ? '/kiosk' : '/ca-home'

  // Read from correct config based on portal
  // For kiosk portal: check kiosk-config first, fallback to ROLE_CA_CONFIG (backward compat)
  // For ca-home portal: use ROLE_CA_CONFIG only (reception tablet flow)
  const kioskConfigStr = basePath === '/kiosk' ? localStorage.getItem('kiosk-config') : null
  const roleCaConfigStr = localStorage.getItem('ROLE_CA_CONFIG')
  const config = kioskConfigStr ? JSON.parse(kioskConfigStr) : (roleCaConfigStr ? JSON.parse(roleCaConfigStr) : {})
  const roleCaConfig = config

  // CA portal has a single pre-defined flow (reception tablet); no flow field.
  const selectedFlow = 'reception-tablet'

  // Appointment screen states
  const [selectedBrandForOOH, setSelectedBrandForOOH] = useState(roleCaConfig?.BRAND_FOR_OOH || '')
  const [selectedLocationForOOH, setSelectedLocationForOOH] = useState(
    roleCaConfig?.LOCATION_FOR_OOH || '',
  )
  const [selectedMentorForOOH, setSelectedMentorForOOH] = useState(roleCaConfig?.MENTOR_FOR_OOH || '')

  // Remote doctor states
  const [selectedScreenConfigForRemoteScreen, setSelectedScreenConfigForRemoteScreen] = useState(
    roleCaConfig?.SCREEN_DEVICE_CONFIG_ID || '',
  )
  const [selectedCurrentVirtualRoomId, setSelectedCurrentVirtualRoomId] = useState(
    roleCaConfig?.CURRENT_VIRTUAL_ROOM_ID || '',
  )

  const [isRegisteringScreen, setIsRegisteringScreen] = useState(false)

  const {
    oohDataLoading,
    screenConfigLoading,
    brands,
    locations,
    mentors,
    screenConfigs,
    virtualConfigs,
  } = useCADeviceData(assistantData)

  const handleContinue = async () => {
    const newRoleCaConfig = { ...roleCaConfig }
    let additionalData = {}
    let redirectUrl = `${window.location.origin}${basePath}`

    switch (selectedFlow) {
      case 'appointment-screen':
        Object.assign(newRoleCaConfig, {
          BRAND_FOR_OOH: selectedBrandForOOH,
          LOCATION_FOR_OOH: selectedLocationForOOH,
          MENTOR_FOR_OOH: selectedMentorForOOH,
        })
        additionalData = {
          clinicBrandId: selectedBrandForOOH,
          clinicLocationId: selectedLocationForOOH,
        }
        // Use slug-based URL for cleaner routing - need to get slugs from IDs
        // For now, use IDs as slugs will be resolved in the component
        // This will be updated once we have access to location and mentor data here
        redirectUrl = `${window.location.origin}${basePath}/display/${selectedLocationForOOH}/${selectedMentorForOOH}`
        break

      case 'remote-doctor':
        Object.assign(newRoleCaConfig, {
          SCREEN_DEVICE_CONFIG_ID: selectedScreenConfigForRemoteScreen,
          CURRENT_VIRTUAL_ROOM_ID: selectedCurrentVirtualRoomId,
        })

        const selectedVirtualRoom = assistantData?.associatedChamberRoomsData?.find(
          (room) => room?.id === selectedCurrentVirtualRoomId,
        )
        const clinicLocation = assistantData?.associatedClinicsData?.find(
          (clinic) => clinic?.id === selectedVirtualRoom?.clinicLocationId,
        )

        additionalData = {
          screenDeviceConfigId: selectedScreenConfigForRemoteScreen,
          chamberRoomId: selectedCurrentVirtualRoomId,
          clinicLocationId: selectedVirtualRoom?.clinicLocationId,
          clinicBrandId: clinicLocation?.clinicBrandId,
        }
        redirectUrl = `${window.location.origin}${basePath}/chamber-screen-home`
        break
      default:
        break
    }

    // Write to correct config based on flow type
    // ROLE_CA_CONFIG is for reception tablet flow, kiosk-config is for kiosk flows
    if (selectedFlow === 'reception-tablet') {
      // Reception tablet flow → ROLE_CA_CONFIG only
      localStorage.setItem('ROLE_CA_CONFIG', JSON.stringify(newRoleCaConfig))
    } else {
      // Kiosk flows (FLOW_REMOTE_DOCTOR, FLOW_APPOINTMENT_SCREEN) → kiosk-config
      localStorage.setItem('kiosk-config', JSON.stringify(newRoleCaConfig))
      // ⚠️ TEMPORARY: Also update ROLE_CA_CONFIG for backward compatibility during migration
      // TODO: Remove this after all existing users are migrated
      localStorage.setItem('ROLE_CA_CONFIG', JSON.stringify(newRoleCaConfig))
    }
    
    setIsRegisteringScreen(true)
    if (selectedFlow === 'remote-doctor') {
      try {
        // First API call - registerScreen
        console.log("Registering from selection")
        const result = await registerScreen(selectedFlow, additionalData)

        if (result.success) {
          console.log('Screen registered successfully:', result.screenId)
        } else {
          console.error('Failed to register screen:', result.error)
        }
      } catch (error) {
        console.error('Error during API calls:', error)
        // Still navigate even if there's an error
      }
    } else {
      await unregisterScreen()
    }
    setIsRegisteringScreen(false)
    window.location.href = redirectUrl
  }

  const isFormValid = () => {
    switch (selectedFlow) {
      case 'reception-tablet':
        return true
      case 'appointment-screen':
        return selectedBrandForOOH && selectedLocationForOOH && selectedMentorForOOH
      case 'remote-doctor':
        return selectedScreenConfigForRemoteScreen && selectedCurrentVirtualRoomId
      default:
        return false
    }
  }

  return (
    <Dialog
      open={CAFlowDialogOpen}
      PaperProps={{
        sx: { padding: '24px', margin: '8px', maxWidth: '600px', borderRadius: '8px' },
      }}
    >
      <CloseIcon
        sx={{ position: 'absolute', top: '16px', right: '16px', cursor: 'pointer' }}
        onClick={() => setCAFlowDialogOpen(false)}
      />

      <div style={{ marginBottom: '24px' }}>
        <div>
          <b>What is this device being used as?</b>
        </div>
        <div style={{ fontSize: '12px' }}>
          <b>(यह डिवाइस किस रूप में उपयोग की जा रही है?)</b>
        </div>
      </div>

      {selectedFlow === 'appointment-screen' && (
        <AppointmentScreenConfig
          loading={oohDataLoading}
          selectedBrand={selectedBrandForOOH}
          selectedLocation={selectedLocationForOOH}
          selectedMentor={selectedMentorForOOH}
          onBrandChange={setSelectedBrandForOOH}
          onLocationChange={setSelectedLocationForOOH}
          onMentorChange={setSelectedMentorForOOH}
          brands={brands}
          locations={locations}
          mentors={mentors}
          assistantData={assistantData}
        />
      )}

      {selectedFlow === 'remote-doctor' && (
        <RemoteDoctorConfig
          loading={screenConfigLoading}
          selectedScreenConfig={selectedScreenConfigForRemoteScreen}
          selectedVirtualRoom={selectedCurrentVirtualRoomId}
          onScreenConfigChange={setSelectedScreenConfigForRemoteScreen}
          onVirtualRoomChange={setSelectedCurrentVirtualRoomId}
          screenConfigs={screenConfigs}
          virtualConfigs={virtualConfigs}
          assistantData={assistantData}
        />
      )}

      <div className="w-100 d-flex justify-content-center">
        <CAFlowSelectionDialogContinueButton
          disabled={!isFormValid() || isRegisteringScreen}
          onClick={handleContinue}
        >
          {isRegisteringScreen ? 'Configuring your device...' : 'Continue'}
        </CAFlowSelectionDialogContinueButton>
      </div>
    </Dialog>
  )
}
